import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileSignature,
  Inbox as InboxIcon,
  GitBranch,
  AlertTriangle,
  Clock,
  Upload,
  Sparkles,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { api, formatDate } from "../lib/api";
import { labelForAction } from "../lib/auditLabels";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../auth/AuthProvider";
import { canManageOrg } from "../lib/permissions";

// Mirror Inbox's TYPE_META labels — the same task type must read the same
// everywhere (the widget used to show raw "sign"/"approval" in English).
const TASK_TYPE_LABELS = {
  approval: "Согласование",
  sign: "Подписание",
  form: "Форма",
};

// ─────────────────────────────────────────────────────────────────────────────
// KPI card
// ─────────────────────────────────────────────────────────────────────────────
function Kpi({ label, value, icon: Icon, accent, testid, to }) {
  const inner = (
    <div
      data-testid={testid}
      className="bg-white border border-zinc-200 rounded-2xl p-5 transition-colors hover:bg-zinc-50/50 h-full"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
          {label}
        </div>
        {Icon && (
          <div
            className={`h-8 w-8 rounded-full flex items-center justify-center ${
              accent || "bg-primary/10 text-primary"
            }`}
          >
            <Icon className="h-4 w-4" strokeWidth={1.9} />
          </div>
        )}
      </div>
      <div className="text-display-md mt-3 text-zinc-900">{value}</div>
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Widget shell
// ─────────────────────────────────────────────────────────────────────────────
function Widget({ title, action, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
    >
      <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900 tracking-tight">
          {title}
        </h3>
        {action}
      </header>
      <div>{children}</div>
    </section>
  );
}

function EmptyRow({ children }) {
  return (
    <div className="px-5 py-8 text-center text-sm text-zinc-500">{children}</div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { role, currentOrg } = useAuth();
  const isAdmin = canManageOrg(role);

  const meQuery = useQuery({
    queryKey: ["dashboard", "me", currentOrg?.id],
    queryFn: () => api.dashboardMe(),
    enabled: Boolean(currentOrg?.id),
    staleTime: 30_000,
  });

  const orgQuery = useQuery({
    queryKey: ["dashboard", "org", currentOrg?.id],
    queryFn: () => api.dashboardOrg(),
    enabled: Boolean(currentOrg?.id && isAdmin),
    staleTime: 30_000,
  });

  const me = meQuery.data;
  const org = orgQuery.data;

  // UX-25: большой маркетинговый блок занимал ~55% первого экрана при КАЖДОМ
  // входе и отодвигал метрики и задачи под сгиб. Для новой организации он
  // полезен как онбординг, для вернувшегося пользователя — нет. Показываем его
  // только пока в организации ещё ничего не происходило (и только когда данные
  // уже загружены, чтобы блок не «прыгал» на каждой загрузке).
  const dataReady = !meQuery.isLoading && (!isAdmin || !orgQuery.isLoading);
  const hasActivity =
    (me?.pending_tasks ?? 0) > 0 ||
    (org?.documents_signed_this_week ?? 0) > 0 ||
    (org?.active_workflow_instances ?? 0) > 0 ||
    (org?.errored_instances ?? 0) > 0 ||
    (me?.tasks?.length ?? 0) > 0;
  const showHero = dataReady && !hasActivity;

  return (
    <div className="space-y-12" data-testid="dashboard-page">
      {showHero ? (
        /* Онбординг-версия: только для «пустой» организации */
        <section className="pt-4" data-testid="dashboard-hero">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-primary font-medium mb-6">
            <ShieldCheck className="h-3.5 w-3.5" />
            ЭЦП · Платформа подписания
          </div>
          <h2 className="text-display-xl text-zinc-900 max-w-4xl">
            Документы, формы<br />и процессы —<br />в одном месте.
          </h2>
          <p className="text-subhead text-zinc-500 mt-6 max-w-2xl">
            Загружайте PDF, проектируйте процессы, отслеживайте задачи и сроки. Всё, что нужно для подписания и согласования.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to="/upload"
              data-testid="hero-upload-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors btn-pill"
            >
              <Upload className="h-4 w-4" />
              Отправить на подпись
            </Link>
            <Link
              to="/templates"
              data-testid="hero-templates-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium bg-zinc-100 text-zinc-900 hover:bg-zinc-200 transition-colors btn-pill"
            >
              <Sparkles className="h-4 w-4" />
              Сгенерировать из шаблона
            </Link>
          </div>
        </section>
      ) : (
        /* Рабочая версия: те же действия, но одной компактной строкой —
           метрики и задачи поднимаются выше сгиба. */
        <section
          className="pt-4 flex flex-wrap items-center justify-between gap-3"
          data-testid="dashboard-compact-header"
        >
          <div>
            <h2 className="text-2xl font-medium tracking-tight text-zinc-900">
              Сводка
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              Задачи, документы и процессы вашей организации.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/upload"
              data-testid="hero-upload-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors btn-pill"
            >
              <Upload className="h-4 w-4" />
              Отправить на подпись
            </Link>
            <Link
              to="/templates"
              data-testid="hero-templates-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-zinc-100 text-zinc-900 hover:bg-zinc-200 transition-colors btn-pill"
            >
              <Sparkles className="h-4 w-4" />
              Сгенерировать из шаблона
            </Link>
          </div>
        </section>
      )}

      {/* KPI cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Подписано за неделю"
          value={isAdmin ? (org?.documents_signed_this_week ?? "—") : "—"}
          icon={FileSignature}
          accent="bg-primary/10 text-primary"
          testid="kpi-signed-week"
          to="/documents?status=completed"
        />
        <Kpi
          label="Мои задачи"
          value={me?.pending_tasks ?? 0}
          icon={InboxIcon}
          accent="bg-amber-100 text-amber-700"
          testid="kpi-pending-tasks"
          to="/inbox"
        />
        <Kpi
          label="Активные процессы"
          value={isAdmin ? (org?.active_workflow_instances ?? "—") : "—"}
          icon={GitBranch}
          accent="bg-emerald-100 text-emerald-700"
          testid="kpi-active-instances"
          to="/workflows"
        />
        <Kpi
          label="Ошибки процессов"
          value={isAdmin ? (org?.errored_instances ?? "—") : "—"}
          icon={AlertTriangle}
          accent="bg-rose-100 text-rose-700"
          testid="kpi-errored-instances"
          to="/workflows"
        />
      </section>

      {/* Three-column widget row */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* My tasks */}
        <Widget
          title="Мои задачи"
          testid="widget-my-tasks"
          action={
            <Link
              to="/inbox"
              data-testid="widget-tasks-all"
              className="text-xs text-primary hover:underline"
            >
              Все →
            </Link>
          }
        >
          {meQuery.isLoading ? (
            <EmptyRow>Загрузка…</EmptyRow>
          ) : !me?.recent_tasks?.length ? (
            <EmptyRow>Задач пока нет</EmptyRow>
          ) : (
            <ul>
              {me.recent_tasks.map((t) => (
                <li
                  key={t.id}
                  data-testid={`widget-task-${t.id}`}
                  className="px-5 py-3 border-b border-zinc-100 last:border-0 flex items-center justify-between gap-3"
                >
                  <Link
                    to={`/inbox?task=${t.id}`}
                    className="flex-1 min-w-0 truncate text-sm text-zinc-900 hover:text-primary"
                  >
                    {t.title}
                  </Link>
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wider shrink-0">
                    {TASK_TYPE_LABELS[t.type] || t.type}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        {/* Active instances */}
        <Widget
          title="Активные процессы"
          testid="widget-active-instances"
          action={
            isAdmin ? (
              <Link
                to="/workflows"
                data-testid="widget-instances-all"
                className="text-xs text-primary hover:underline"
              >
                Все →
              </Link>
            ) : null
          }
        >
          {!isAdmin ? (
            <EmptyRow>Доступно администраторам</EmptyRow>
          ) : orgQuery.isLoading ? (
            <EmptyRow>Загрузка…</EmptyRow>
          ) : !org?.recent_instances?.length ? (
            <EmptyRow>Нет активных процессов</EmptyRow>
          ) : (
            <ul>
              {org.recent_instances.map((inst) => (
                <li
                  key={inst.id}
                  data-testid={`widget-instance-${inst.id}`}
                  className="px-5 py-3 border-b border-zinc-100 last:border-0"
                >
                  <Link
                    to={`/workflow-instances/${inst.id}`}
                    className="block hover:text-primary"
                  >
                    <div className="text-sm text-zinc-900 truncate">
                      {inst.workflow_id}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {formatDate(inst.started_at)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        {/* Recent activity */}
        <Widget
          title="Недавняя активность"
          testid="widget-recent-activity"
          action={
            isAdmin ? (
              <Link
                to="/settings/audit"
                data-testid="widget-activity-all"
                className="text-xs text-primary hover:underline"
              >
                Журнал →
              </Link>
            ) : null
          }
        >
          {!isAdmin ? (
            <EmptyRow>Доступно администраторам</EmptyRow>
          ) : orgQuery.isLoading ? (
            <EmptyRow>Загрузка…</EmptyRow>
          ) : !org?.recent_activity?.length ? (
            <EmptyRow>История пуста</EmptyRow>
          ) : (
            <ul>
              {org.recent_activity.map((e) => (
                <li
                  key={e.id}
                  data-testid={`widget-event-${e.id}`}
                  className="px-5 py-3 border-b border-zinc-100 last:border-0"
                >
                  <div className="flex items-start gap-3">
                    <Activity className="h-3.5 w-3.5 mt-1 text-zinc-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900 truncate">
                        {labelForAction(e.action)}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {formatDate(e.created_at)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Widget>
      </section>

      {/* Recent documents — keep existing affordance for owners/operators */}
      {me?.recent_documents?.length > 0 && (
        <section data-testid="recent-section">
          <div className="flex items-end justify-between mb-5">
            <h3 className="text-display-md text-zinc-900">Мои недавние документы</h3>
            <Link
              to="/documents"
              data-testid="see-all-docs-link"
              className="text-sm text-primary hover:underline"
            >
              Все документы →
            </Link>
          </div>
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-200 bg-surface-soft">
                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                  <th className="px-5 py-4 font-medium">Документ</th>
                  <th className="px-5 py-4 font-medium">Статус</th>
                  <th className="px-5 py-4 font-medium">Обновлён</th>
                </tr>
              </thead>
              <tbody>
                {me.recent_documents.map((doc) => (
                  <tr
                    key={doc.id}
                    data-testid={`recent-row-${doc.id}`}
                    className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors cursor-pointer"
                    onClick={() => (window.location.href = `/documents/${doc.id}`)}
                  >
                    <td className="px-5 py-4 font-medium text-zinc-900">
                      {doc.title}
                    </td>
                    <td className="px-5 py-4">
                      <StatusBadge status={doc.status} />
                    </td>
                    <td className="px-5 py-4 text-zinc-400 text-xs">
                      {formatDate(doc.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
